import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAirQualityStore } from '../../stores/useAirQualityStore';
import { useAuthStore } from '../../stores';
import { fetchAirQuality, fetchWeatherData, type WeatherData } from '../../services/api/airQualityApi';
import { supabase } from '../../services/supabase/client';

interface LandingPageProps {
  onEnterApp: () => void;
}

export function LandingPage({ onEnterApp }: LandingPageProps) {
  const navigate = useNavigate();
  const { isAuthenticated, signOut } = useAuthStore();
  const [routeQuery, setRouteQuery] = useState('');
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [showChallenge, setShowChallenge] = useState(false);
  const [challengeForm, setChallengeForm] = useState({ name: '', email: '', instagram: '' });
  const [challengeStatus, setChallengeStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const [showScentsWaitlist, setShowScentsWaitlist] = useState(false);
  const [scentsForm, setScentsForm] = useState({ name: '', email: '', company: '', designation: '', dob: '' });
  const [scentsStatus, setScentsStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const airQuality = useAirQualityStore((state) => state.currentReading);
  const setAirQuality = useAirQualityStore((state) => state.setAirQuality);
  const pm25 = useAirQualityStore((state) => state.pm25);


  const handleRouteSearch = () => {
    if (routeQuery.trim()) {
      onEnterApp();
      navigate(`/plan-route?q=${encodeURIComponent(routeQuery)}`);
    }
  };

  const handleChallengeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challengeForm.name.trim() || !challengeForm.email.trim()) return;
    setChallengeStatus('submitting');
    try {
      await supabase.from('challenge_registrations').insert({
        name: challengeForm.name.trim(),
        email: challengeForm.email.trim(),
        instagram: challengeForm.instagram.trim() || null,
      });
      setChallengeStatus('success');
    } catch {
      setChallengeStatus('error');
    }
  };

  const handleScentsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!scentsForm.name.trim() || !scentsForm.email.trim()) return;
    setScentsStatus('submitting');
    try {
      await supabase.from('scents_waitlist').insert({
        name: scentsForm.name.trim(),
        email: scentsForm.email.trim(),
        company: scentsForm.company.trim() || null,
        designation: scentsForm.designation.trim(),
        date_of_birth: scentsForm.dob || null,
      });
      setScentsStatus('success');
    } catch {
      setScentsStatus('error');
    }
  };

  // Auto-open challenge form if ?challenge=true in URL (for QR code scanning)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('challenge') === 'true') {
      setShowChallenge(true);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            const [aqData, weatherData] = await Promise.all([
              fetchAirQuality(latitude, longitude),
              fetchWeatherData(latitude, longitude),
            ]);
            if (aqData) setAirQuality(aqData);
            if (weatherData) setWeather(weatherData);
          },
          async () => {
            const [aqData, weatherData] = await Promise.all([
              fetchAirQuality(3.139, 101.6869),
              fetchWeatherData(3.139, 101.6869),
            ]);
            if (aqData) setAirQuality(aqData);
            if (weatherData) setWeather(weatherData);
          },
          { enableHighAccuracy: true, timeout: 5000 }
        );
      }
    };
    fetchData();
  }, [setAirQuality]);

  const aqi = airQuality?.aqi ?? 42;
  const temp = weather?.temperature ?? 28;
  const aqiLabel = aqi <= 50 ? 'Good' : aqi <= 100 ? 'Moderate' : 'Poor';
  const aqiColor = aqi <= 50 ? '#22c55e' : aqi <= 100 ? '#eab308' : '#ef4444';
  const pm25Val = pm25?.value ?? 15;
  const pm25Color = pm25Val <= 12 ? '#22c55e' : pm25Val <= 35.4 ? '#eab308' : '#ef4444';

  return (
    <div className="min-h-screen bg-[#050507] text-white overflow-x-hidden selection:bg-[#00ffd0]/20 selection:text-white">

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-40 px-6 lg:px-12 py-5">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Blurp.ie" className="w-8 h-8 object-contain" />
            <span className="text-[15px] font-medium tracking-tight text-white/80">Blurp.ie</span>
          </div>
          <div className="flex items-center gap-3">
            {isAuthenticated && (
              <button
                onClick={async () => {
                  await signOut();
                  localStorage.removeItem('blurp-auth-store');
                  localStorage.removeItem('blurp-subscription-store');
                  window.location.href = '/';
                }}
                className="px-4 py-2 text-[13px] text-white/30 hover:text-white/60 transition-colors"
              >
                Sign Out
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* ─── Hero: Split Layout ─── */}
      <section className="relative min-h-screen flex items-center px-6 lg:px-12 pt-24 pb-32">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-8 items-center">

            {/* Left: Text */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              {/* Live indicator */}
              <div className="flex items-center gap-4 mb-10">
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: aqiColor }} />
                    <div className="absolute inset-0 w-1.5 h-1.5 rounded-full animate-ping opacity-40" style={{ backgroundColor: aqiColor }} />
                  </div>
                  <span className="text-[11px] text-white/40 tracking-[0.15em] uppercase font-medium">Live</span>
                </div>

                <div className="h-3 w-px bg-white/10" />

                <div className="flex items-center gap-1.5">
                  <span className="text-[12px] text-white/60 font-medium">AQI</span>
                  <span className="text-[12px] font-bold" style={{ color: aqiColor }}>{aqi}</span>
                  <span className="text-[11px] font-medium" style={{ color: aqiColor }}>{aqiLabel}</span>
                </div>

                <div className="h-3 w-px bg-white/10" />

                <div className="flex items-center gap-1.5">
                  <span className="text-[12px] text-white/60 font-medium">PM2.5</span>
                  <span className="text-[12px] font-bold" style={{ color: pm25Color }}>{Math.round(pm25Val)} µg/m³</span>
                </div>

                <div className="h-3 w-px bg-white/10" />

                <div className="flex items-center gap-1.5">
                  <span className="text-[12px] text-white/60 font-medium">{Math.round(temp)}°C</span>
                </div>
              </div>

              <h1 className="text-[clamp(2.5rem,6vw,5rem)] font-bold leading-[1.05] tracking-[-0.03em] mb-8">
                <span className="text-white">Better air,</span>
                <br />
                <span className="text-[#00ffd0]">better runs.</span>
              </h1>

              <p className="text-[15px] md:text-base text-white/35 max-w-sm leading-[1.7] mb-10">
                Air quality data, route planning, and performance tracking for runners who care about what they breathe.
              </p>

              <div className="flex items-center gap-4">
                <a
                  href="#product"
                  className="text-[13px] text-white/25 hover:text-white/50 transition-colors"
                >
                  See how it works
                </a>
              </div>
            </motion.div>

            {/* Right: Phone */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="flex justify-center lg:justify-end"
            >
              <div className="relative w-[260px] sm:w-[280px]">
                {/* Subtle glow behind phone */}
                <div className="absolute -inset-16 bg-[#00ffd0]/[0.03] blur-[80px] rounded-full" />

                {/* Phone */}
                <div className="relative rounded-[2.5rem] p-[3px] bg-gradient-to-b from-white/[0.06] to-transparent">
                  <div className="rounded-[2.4rem] overflow-hidden bg-black">
                    <video
                      autoPlay loop muted playsInline
                      className="w-full h-auto block"
                      style={{ aspectRatio: '384/848' }}
                    >
                      <source src="/demo.mov" type="video/quicktime" />
                      <source src="/demo.mov" type="video/mp4" />
                    </video>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── Product: Value Propositions ─── */}
      <section id="product" className="relative py-32 lg:py-40 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">

          {/* Section header · left aligned */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-20 max-w-xl"
          >
            <p className="text-[11px] text-white/20 tracking-[0.2em] uppercase font-medium mb-4">What we do</p>
            <h2 className="text-2xl md:text-3xl font-bold leading-[1.3] tracking-[-0.02em] text-white/90">
              Every breath counts when you're pushing limits. We help you run when the air works for you, not against you.
            </h2>
          </motion.div>

          {/* Features · horizontal layout, not a grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.04] rounded-2xl overflow-hidden">
            {[
              {
                title: 'Air Intelligence',
                desc: 'Live AQI, PM2.5, humidity, and temperature for your exact location.',
              },
              {
                title: 'Route Optimization',
                desc: 'AI-powered routes that avoid pollution hotspots and favor cleaner air corridors.',
              },
              {
                title: 'Performance Tracking',
                desc: 'Correlate your splits with air conditions. Find the patterns behind your best runs.',
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-[#050507] p-8 lg:p-10"
              >
                <div className="text-[11px] text-[#00ffd0]/50 tracking-[0.15em] uppercase font-medium mb-4">
                  0{i + 1}
                </div>
                <h3 className="text-[15px] font-semibold text-white/80 mb-3">{item.title}</h3>
                <p className="text-[13px] text-white/25 leading-[1.7]">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Statement ─── */}
      <section className="relative py-32 lg:py-40 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold leading-[1.2] tracking-[-0.03em] text-white/90">
              You track your pace, your heart rate, your cadence.
              <span className="text-white/25"> Why not track the air you breathe?</span>
            </h2>
          </motion.div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="relative py-32 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8 py-12 border-t border-white/[0.06]"
          >
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-white/90 mb-2">
                Ready when you are.
              </h2>
              <p className="text-[13px] text-white/25">Free to start. No credit card required.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 lg:px-12 py-8 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Blurp.ie" className="w-5 h-5 object-contain opacity-40" />
            <span className="text-[12px] text-white/20">Blurp.ie</span>
          </div>
          <span className="text-[11px] text-white/15">&copy; 2026</span>
        </div>
      </footer>

      {/* ─── Challenge Interest Form Modal ─── */}
      <AnimatePresence>
        {showChallenge && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center px-4"
            onClick={() => setShowChallenge(false)}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md bg-[#0a0a0f] border border-white/[0.08] rounded-2xl overflow-hidden shadow-2xl shadow-black/50"
            >
              {/* Close button */}
              <button
                onClick={() => setShowChallenge(false)}
                className="absolute top-4 right-4 z-10 p-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.12] transition-colors"
              >
                <svg className="w-4 h-4 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Poster */}
              <div className="w-full bg-[#111] border-b border-white/[0.06]">
                <img
                  src="/challenge-poster.png"
                  alt="Horseperity Challenge"
                  className="w-full h-auto object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              </div>

              {/* Form */}
              <div className="p-6">
                {challengeStatus === 'success' ? (
                  <div className="text-center py-8">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#00ffd0]/10 flex items-center justify-center">
                      <svg className="w-7 h-7 text-[#00ffd0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">You're registered!</h3>
                    <p className="text-[13px] text-white/40 leading-relaxed">We'll notify you with event details. See you at the start line.</p>
                  </div>
                ) : (
                  <>
                    <h3 className="text-lg font-bold text-white mb-1">6.66km Horseperity Challenge</h3>
                    <p className="text-[13px] text-white/30 mb-6">Register your interest below.</p>

                    <form onSubmit={handleChallengeSubmit} className="space-y-3">
                      <input
                        type="text"
                        placeholder="Full name"
                        required
                        value={challengeForm.name}
                        onChange={(e) => setChallengeForm(f => ({ ...f, name: e.target.value }))}
                        className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-[14px] text-white placeholder-white/20 outline-none focus:border-[#00ffd0]/40 transition-colors"
                      />
                      <input
                        type="email"
                        placeholder="Email address"
                        required
                        value={challengeForm.email}
                        onChange={(e) => setChallengeForm(f => ({ ...f, email: e.target.value }))}
                        className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-[14px] text-white placeholder-white/20 outline-none focus:border-[#00ffd0]/40 transition-colors"
                      />
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[14px] text-white/20">@</span>
                        <input
                          type="text"
                          placeholder="Instagram handle"
                          required
                          value={challengeForm.instagram}
                          onChange={(e) => setChallengeForm(f => ({ ...f, instagram: e.target.value }))}
                          className="w-full pl-9 pr-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-[14px] text-white placeholder-white/20 outline-none focus:border-[#00ffd0]/40 transition-colors"
                        />
                      </div>

                      {challengeStatus === 'error' && (
                        <p className="text-[12px] text-red-400">Something went wrong. Please try again.</p>
                      )}

                      <button
                        type="submit"
                        disabled={challengeStatus === 'submitting'}
                        className="w-full py-3 mt-2 rounded-lg text-[13px] font-semibold tracking-wide text-[#050507] bg-[#00ffd0] hover:bg-[#00ffd0]/85 disabled:opacity-50 transition-all duration-300"
                      >
                        {challengeStatus === 'submitting' ? 'Submitting...' : 'Register Interest'}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Internet of Scents Waitlist Modal ─── */}
      <AnimatePresence>
        {showScentsWaitlist && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center px-4"
            onClick={() => setShowScentsWaitlist(false)}
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md bg-[#0a0a0f] border border-purple-500/20 rounded-2xl overflow-hidden shadow-2xl shadow-black/50"
            >
              {/* Close */}
              <button
                onClick={() => setShowScentsWaitlist(false)}
                className="absolute top-4 right-4 z-10 p-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.12] transition-colors"
              >
                <svg className="w-4 h-4 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Header */}
              <div className="px-6 pt-8 pb-4 border-b border-white/[0.06]">
                <p className="text-[10px] text-purple-400/60 tracking-[0.2em] uppercase font-medium mb-2">Coming Soon</p>
                <h3 className="text-lg font-bold text-white mb-1">World's First Internet of Scents</h3>
                <p className="text-[13px] text-white/30">Join the waitlist. Be first to experience it.</p>
              </div>

              {/* Form */}
              <div className="p-6">
                {scentsStatus === 'success' ? (
                  <div className="text-center py-8">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-purple-500/10 flex items-center justify-center">
                      <svg className="w-7 h-7 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">You're on the list!</h3>
                    <p className="text-[13px] text-white/40 leading-relaxed">We'll reach out when we're ready to launch. Stay tuned.</p>
                  </div>
                ) : (
                  <form onSubmit={handleScentsSubmit} className="space-y-3">
                    <div>
                      <label className="block text-[11px] text-white/40 mb-1.5 ml-1">Full name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        placeholder="Full name"
                        required
                        value={scentsForm.name}
                        onChange={(e) => setScentsForm(f => ({ ...f, name: e.target.value }))}
                        className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-[14px] text-white placeholder-white/20 outline-none focus:border-purple-500/40 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-white/40 mb-1.5 ml-1">Email address <span className="text-red-500">*</span></label>
                      <input
                        type="email"
                        placeholder="Email address"
                        required
                        value={scentsForm.email}
                        onChange={(e) => setScentsForm(f => ({ ...f, email: e.target.value }))}
                        className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-[14px] text-white placeholder-white/20 outline-none focus:border-purple-500/40 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-white/40 mb-1.5 ml-1">Designation <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        placeholder="e.g. CTO, Researcher, Student"
                        required
                        value={scentsForm.designation}
                        onChange={(e) => setScentsForm(f => ({ ...f, designation: e.target.value }))}
                        className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-[14px] text-white placeholder-white/20 outline-none focus:border-purple-500/40 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-white/40 mb-1.5 ml-1">Company <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        placeholder="Your company or organisation"
                        required
                        value={scentsForm.company}
                        onChange={(e) => setScentsForm(f => ({ ...f, company: e.target.value }))}
                        className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-[14px] text-white placeholder-white/20 outline-none focus:border-purple-500/40 transition-colors"
                      />
                    </div>
                    <div className="relative">
                      <label className="block text-[11px] text-white/40 mb-1.5 ml-1">Date of birth <span className="text-red-500">*</span></label>
                      <input
                        type="date"
                        required
                        value={scentsForm.dob}
                        onChange={(e) => setScentsForm(f => ({ ...f, dob: e.target.value }))}
                        className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-[14px] text-white/70 outline-none focus:border-purple-500/40 transition-colors [color-scheme:dark]"
                      />
                    </div>

                    {scentsStatus === 'error' && (
                      <p className="text-[12px] text-red-400">Something went wrong. Please try again.</p>
                    )}

                    <button
                      type="submit"
                      disabled={scentsStatus === 'submitting'}
                      className="w-full py-3 mt-2 rounded-lg text-[13px] font-semibold tracking-wide text-white bg-purple-600 hover:bg-purple-500 disabled:opacity-50 transition-all duration-300"
                    >
                      {scentsStatus === 'submitting' ? 'Submitting...' : 'Join Waitlist'}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search bar · fixed bottom */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 pb-5 pt-10 px-6"
        style={{ background: 'linear-gradient(to top, rgba(5,5,7,0.98) 0%, rgba(5,5,7,0.8) 70%, transparent 100%)' }}
      >
        <div className="max-w-xl mx-auto">
          <div className="flex items-center gap-2 bg-white border border-white/80 rounded-xl p-2 hover:border-[#00ffd0] focus-within:border-[#00ffd0] transition-all duration-300 shadow-lg shadow-black/20">
            <svg className="w-4 h-4 text-gray-400 ml-2 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            <input
              type="text"
              value={routeQuery}
              onChange={(e) => setRouteQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleRouteSearch();
                }
              }}
              placeholder="Plan a route, e.g. 'shady path near parks'"
              className="flex-1 bg-transparent text-gray-900 placeholder-gray-400 px-2 py-2.5 outline-none text-sm"
            />
            <button
              onClick={handleRouteSearch}
              disabled={!routeQuery.trim()}
              className="shrink-0 px-4 py-2 rounded-lg text-[12px] font-medium bg-[#00ffd0] text-[#050507] hover:bg-[#00ffd0]/80 disabled:bg-white/[0.04] disabled:text-white/20 flex items-center justify-center gap-1.5 transition-all duration-200"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
              Go
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
